import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { materialApoioAPI, formatarErroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  BookOpen, Upload, Download, Eye, FileText, Image as ImageIcon, Search, Loader2, Pencil, Trash2, Sparkles, GraduationCap, X,
} from 'lucide-react';

const CATEGORIAS_SUGERIDAS = ['Treinamento', 'Planos e Preços', 'Scripts de Venda', 'Procedimentos', 'Portabilidade', 'Documentação', 'Geral'];

const fmtTamanho = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtData = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');
const isNovo = (iso) => iso && Date.now() - new Date(iso).getTime() < 7 * 24 * 3600 * 1000;

const abrirBlob = (blob, nome, baixar) => {
  const url = URL.createObjectURL(blob);
  if (baixar) {
    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } else {
    window.open(url, '_blank', 'noopener');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const Miniatura = ({ item, src }) => {
  if (item.tipo === 'imagem' && src) {
    return <img src={src} alt={item.titulo} className="w-full h-40 object-cover" loading="lazy" />;
  }
  return (
    <div className={`w-full h-40 flex flex-col items-center justify-center gap-2 ${item.tipo === 'pdf' ? 'bg-gradient-to-br from-red-900/70 to-[#1a1d29]' : 'bg-gradient-to-br from-purple-900/70 to-[#1a1d29]'}`}>
      {item.tipo === 'pdf' ? <FileText className="w-12 h-12 text-red-300" /> : <ImageIcon className="w-12 h-12 text-purple-300" />}
      <span className="text-[11px] uppercase tracking-widest text-white/60">{item.tipo === 'pdf' ? 'Documento PDF' : 'Imagem'}</span>
    </div>
  );
};

const CardMaterial = ({ item, thumb, isAdmin, onVer, onBaixar, onEditar, onExcluir, ocupado }) => (
  <div className="group rounded-xl border bg-card overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`material-card-${item.id}`}>
    <div className="relative cursor-pointer" onClick={() => onVer(item)}>
      <Miniatura item={item} src={thumb} />
      <div className="absolute top-2 left-2 flex gap-1">
        <Badge className="bg-black/60 text-white border-0 backdrop-blur text-[10px]">{item.categoria || 'Geral'}</Badge>
        {isNovo(item.criado_em) && <Badge className="bg-emerald-500 text-white border-0 text-[10px]" data-testid={`material-novo-${item.id}`}><Sparkles className="w-3 h-3 mr-0.5" /> Novo</Badge>}
      </div>
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
        <span className="text-white text-sm font-semibold flex items-center gap-1"><Eye className="w-4 h-4" /> Visualizar</span>
      </div>
    </div>
    <div className="p-3 space-y-2">
      <h3 className="font-semibold text-sm leading-snug line-clamp-2" title={item.titulo} data-testid={`material-titulo-${item.id}`}>{item.titulo}</h3>
      {item.descricao && <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2" title={item.descricao}>{item.descricao}</p>}
      <p className="text-[11px] text-gray-400">{fmtTamanho(item.tamanho)} · {fmtData(item.criado_em)} · {item.downloads || 0} download{item.downloads === 1 ? '' : 's'}</p>
      <div className="flex items-center gap-1.5 pt-1">
        <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => onVer(item)} disabled={ocupado === item.id} data-testid={`material-ver-${item.id}`}>
          <Eye className="w-3.5 h-3.5 mr-1" /> Ver
        </Button>
        <Button size="sm" className="flex-1 h-8 text-xs bg-purple-600 hover:bg-purple-700" onClick={() => onBaixar(item)} disabled={ocupado === item.id} data-testid={`material-baixar-${item.id}`}>
          {ocupado === item.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Baixar
        </Button>
        {isAdmin && (
          <>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEditar(item)} title="Editar" data-testid={`material-editar-${item.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => onExcluir(item)} title="Excluir" data-testid={`material-excluir-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </>
        )}
      </div>
    </div>
  </div>
);

const DialogEnvio = ({ aberto, onFechar, onEnviado, categorias }) => {
  const [arquivo, setArquivo] = useState(null);
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState('Treinamento');
  const [progresso, setProgresso] = useState(null);
  const inputRef = useRef(null);

  const escolher = (f) => {
    if (!f) return;
    setArquivo(f);
    if (!titulo) setTitulo(f.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '));
  };

  const enviar = async () => {
    if (!arquivo) return toast.error('Selecione um PDF ou imagem');
    if (!titulo.trim()) return toast.error('Informe o título');
    const fd = new FormData();
    fd.append('arquivo', arquivo);
    fd.append('titulo', titulo.trim());
    fd.append('descricao', descricao.trim());
    fd.append('categoria', categoria.trim() || 'Geral');
    setProgresso(0);
    try {
      await materialApoioAPI.enviar(fd, setProgresso);
      toast.success('Material publicado — todos os usuários foram avisados');
      setArquivo(null); setTitulo(''); setDescricao('');
      onEnviado();
      onFechar();
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setProgresso(null);
    }
  };

  const sugestoes = Array.from(new Set([...CATEGORIAS_SUGERIDAS, ...categorias.map((c) => c.nome)]));

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && progresso === null && onFechar()}>
      <DialogContent className="max-w-lg" data-testid="material-dialog-envio">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-purple-600" /> Publicar material de apoio</DialogTitle>
          <DialogDescription>PDF ou imagem (até 30MB). Fica disponível para todos os usuários baixarem.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); escolher(e.dataTransfer.files?.[0]); }}
            className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${arquivo ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/10' : 'border-purple-300 hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/10'}`}
            data-testid="material-dropzone"
          >
            <input ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,application/pdf,image/*" className="hidden" onChange={(e) => escolher(e.target.files?.[0])} data-testid="material-file-input" />
            {arquivo ? (
              <div className="flex items-center justify-center gap-2 text-sm">
                {arquivo.type === 'application/pdf' ? <FileText className="w-5 h-5 text-red-500" /> : <ImageIcon className="w-5 h-5 text-purple-500" />}
                <span className="font-medium truncate max-w-[280px]">{arquivo.name}</span>
                <span className="text-gray-500">({fmtTamanho(arquivo.size)})</span>
                <button onClick={(e) => { e.stopPropagation(); setArquivo(null); }} className="text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 mx-auto text-purple-400 mb-2" />
                <p className="text-sm font-medium">Arraste o arquivo aqui ou clique para escolher</p>
                <p className="text-xs text-gray-500 mt-1">PDF, PNG, JPG, WEBP ou GIF</p>
              </>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mat-titulo">Título *</Label>
            <Input id="mat-titulo" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ex.: Guia de portabilidade móvel" data-testid="material-titulo-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mat-desc">Descrição (o que o colaborador vai aprender)</Label>
            <textarea id="mat-desc" value={descricao} onChange={(e) => setDescricao(e.target.value)} rows={2} placeholder="Resumo rápido do conteúdo" className="w-full rounded-md border bg-background px-3 py-2 text-sm" data-testid="material-descricao-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mat-cat">Categoria</Label>
            <Input id="mat-cat" list="mat-categorias" value={categoria} onChange={(e) => setCategoria(e.target.value)} data-testid="material-categoria-input" />
            <datalist id="mat-categorias">{sugestoes.map((c) => <option key={c} value={c} />)}</datalist>
          </div>
          {progresso !== null && (
            <div data-testid="material-progresso">
              <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Enviando...</span><span>{progresso}%</span></div>
              <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden"><div className="h-full bg-purple-600 transition-all" style={{ width: `${progresso}%` }} /></div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onFechar} disabled={progresso !== null}>Cancelar</Button>
          <Button onClick={enviar} disabled={progresso !== null || !arquivo} className="bg-purple-600 hover:bg-purple-700" data-testid="material-publicar-btn">
            {progresso !== null ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Publicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DialogEdicao = ({ item, onFechar, onSalvo }) => {
  const [form, setForm] = useState({ titulo: item?.titulo || '', descricao: item?.descricao || '', categoria: item?.categoria || 'Geral' });
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { if (item) setForm({ titulo: item.titulo || '', descricao: item.descricao || '', categoria: item.categoria || 'Geral' }); }, [item]);
  const salvar = async () => {
    setSalvando(true);
    try {
      await materialApoioAPI.editar(item.id, form);
      toast.success('Material atualizado');
      onSalvo();
      onFechar();
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="max-w-md" data-testid="material-dialog-edicao">
        <DialogHeader><DialogTitle>Editar material</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Título</Label><Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} data-testid="material-edit-titulo" /></div>
          <div className="space-y-1.5"><Label>Descrição</Label><textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} rows={2} className="w-full rounded-md border bg-background px-3 py-2 text-sm" data-testid="material-edit-descricao" /></div>
          <div className="space-y-1.5"><Label>Categoria</Label><Input list="mat-categorias-edit" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} data-testid="material-edit-categoria" /><datalist id="mat-categorias-edit">{CATEGORIAS_SUGERIDAS.map((c) => <option key={c} value={c} />)}</datalist></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button onClick={salvar} disabled={salvando} className="bg-purple-600 hover:bg-purple-700" data-testid="material-edit-salvar">{salvando && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MaterialApoio = () => {
  const { user } = useAuth();
  const isAdmin = user?.perfil === 'ADMIN';
  const [itens, setItens] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState('');
  const [tipo, setTipo] = useState('');
  const [thumbs, setThumbs] = useState({});
  const [ocupado, setOcupado] = useState(null);
  const [envioAberto, setEnvioAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const thumbsRef = useRef({});

  const carregar = useCallback(async () => {
    try {
      const res = await materialApoioAPI.listar({ busca: busca || undefined, categoria: categoria || undefined, tipo: tipo || undefined });
      setItens(res.data.itens);
      setCategorias(res.data.categorias);
    } catch (e) {
      toast.error('Erro ao carregar materiais');
    } finally {
      setLoading(false);
    }
  }, [busca, categoria, tipo]);

  useEffect(() => {
    const t = setTimeout(carregar, busca ? 350 : 0);
    return () => clearTimeout(t);
  }, [carregar, busca]);

  // Miniaturas das imagens (arquivo protegido por token → blob)
  useEffect(() => {
    itens.filter((i) => i.tipo === 'imagem' && !thumbsRef.current[i.id]).slice(0, 24).forEach(async (i) => {
      try {
        const res = await materialApoioAPI.arquivo(i.id, 'thumb');
        thumbsRef.current[i.id] = URL.createObjectURL(res.data);
        setThumbs({ ...thumbsRef.current });
      } catch (e) {}
    });
  }, [itens]);

  const entregar = async (item, baixar) => {
    setOcupado(item.id);
    try {
      const res = await materialApoioAPI.arquivo(item.id, baixar ? 'download' : 'inline');
      abrirBlob(res.data, item.nome_arquivo, baixar);
      if (baixar) setItens((prev) => prev.map((i) => (i.id === item.id ? { ...i, downloads: (i.downloads || 0) + 1 } : i)));
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setOcupado(null);
    }
  };

  const excluir = async (item) => {
    if (!window.confirm(`Excluir "${item.titulo}"? Os usuários não poderão mais baixar.`)) return;
    try {
      await materialApoioAPI.excluir(item.id);
      toast.success('Material excluído');
      carregar();
    } catch (e) {
      toast.error(formatarErroAPI(e));
    }
  };

  const totalPdf = itens.filter((i) => i.tipo === 'pdf').length;
  const totalImg = itens.length - totalPdf;

  return (
    <div className="space-y-6" data-testid="material-apoio-page">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#2a0a3d] via-[#3b0f5c] to-[#5a1030] p-6 text-white">
        <GraduationCap className="absolute -right-6 -top-6 w-44 h-44 text-white/5" />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><BookOpen className="w-7 h-7" /> Material de Apoio</h1>
            <p className="text-white/70 text-sm mt-1 max-w-2xl">Conteúdo educativo para a equipe: manuais, tabelas de planos, scripts de venda e procedimentos. Visualize na hora ou baixe em PDF e imagem.</p>
            <p className="text-white/60 text-xs mt-2" data-testid="material-resumo">{itens.length} {itens.length === 1 ? 'material' : 'materiais'} · {totalPdf} PDF · {totalImg} {totalImg === 1 ? 'imagem' : 'imagens'}</p>
          </div>
          {isAdmin && (
            <Button onClick={() => setEnvioAberto(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shrink-0" data-testid="material-novo-btn">
              <Upload className="w-4 h-4 mr-2" /> Publicar material
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por título, descrição ou categoria..." className="pl-9" data-testid="material-busca-input" />
        </div>
        <div className="flex gap-1 rounded-lg border p-1 text-xs" data-testid="material-filtro-tipo">
          {[['', 'Todos'], ['pdf', 'PDF'], ['imagem', 'Imagens']].map(([v, l]) => (
            <button key={v} onClick={() => setTipo(v)} className={`px-3 py-1.5 rounded-md font-medium transition-colors ${tipo === v ? 'bg-purple-600 text-white' : 'hover:bg-purple-50 dark:hover:bg-purple-900/20'}`} data-testid={`material-tipo-${v || 'todos'}`}>{l}</button>
          ))}
        </div>
      </div>

      {categorias.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="material-categorias">
          <button onClick={() => setCategoria('')} className={`px-3 py-1 rounded-full text-xs border transition-colors ${!categoria ? 'bg-purple-600 text-white border-purple-600' : 'hover:bg-purple-50 dark:hover:bg-purple-900/20'}`} data-testid="material-categoria-todas">Todas</button>
          {categorias.map((c) => (
            <button key={c.nome} onClick={() => setCategoria(categoria === c.nome ? '' : c.nome)} className={`px-3 py-1 rounded-full text-xs border transition-colors ${categoria === c.nome ? 'bg-purple-600 text-white border-purple-600' : 'hover:bg-purple-50 dark:hover:bg-purple-900/20'}`} data-testid={`material-categoria-${c.nome}`}>
              {c.nome} <span className="opacity-60">({c.total})</span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>
      ) : itens.length === 0 ? (
        <div className="text-center py-20 rounded-2xl border border-dashed" data-testid="material-vazio">
          <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="font-medium">Nenhum material encontrado</p>
          <p className="text-sm text-gray-500 mt-1">{isAdmin ? 'Clique em "Publicar material" para enviar o primeiro PDF ou imagem.' : 'Em breve o administrador publicará conteúdos aqui.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4" data-testid="material-grid">
          {itens.map((item) => (
            <CardMaterial key={item.id} item={item} thumb={thumbs[item.id]} isAdmin={isAdmin} ocupado={ocupado}
              onVer={(i) => entregar(i, false)} onBaixar={(i) => entregar(i, true)} onEditar={setEditando} onExcluir={excluir} />
          ))}
        </div>
      )}

      {isAdmin && <DialogEnvio aberto={envioAberto} onFechar={() => setEnvioAberto(false)} onEnviado={carregar} categorias={categorias} />}
      {isAdmin && <DialogEdicao item={editando} onFechar={() => setEditando(null)} onSalvo={carregar} />}
    </div>
  );
};

export default MaterialApoio;
